import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Clock, MapPin, FileText, ShieldCheck, LifeBuoy, Network, Code2 } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal, { EASE } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ContactForm from "@/components/ContactForm";
import { services } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { "managed-it-support": LifeBuoy, "cloud-cybersecurity": ShieldCheck, "network-infrastructure": Network, "custom-software": Code2 };
const MotionLink = motion.create(Link);

const KineticLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const marqueeItems = [
  "Managed IT Support", "Cybersecurity", "Cloud & Microsoft 365", "Network & Infrastructure",
  "Custom Software & AI", "Adelaide-Based Engineers", "No Lock-In Contracts", "Plain English, Always",
];

const trustItems = [
  "Local Adelaide office & helpdesk",
  "Security aligned to the ACSC Essential Eight",
  "Microsoft 365 & Google Cloud expertise",
  "Proactive monitoring & maintenance",
];

const whyPoints = [
  { title: "Adelaide-local engineers", desc: "No outsourced offshore call centres. You talk directly to technicians who know South Australian business — and can be on-site when it counts." },
  { title: "Zero jargon, no hidden fees", desc: "Plain-English advice, clear service commitments and predictable monthly costs. You'll always know what you're paying for and why." },
  { title: "Proactive, not reactive", desc: "We proactively monitor your systems and fix issues before they become outages, so your team just gets on with work." },
  { title: "Built for SME budgets", desc: "Enterprise-grade tools and security, right-sized for businesses with 5 to 150 employees. No paying for complexity you'll never use." },
];

const steps = [
  { num: "01", name: "Free 30-minute discovery", desc: "We talk through your current setup, pain points and goals — over coffee or a video call. No jargon, no pressure." },
  { num: "02", name: "Plain-English IT roadmap", desc: "You receive a concise proposal: what's urgent, what can wait, and what it costs. Yours to keep either way." },
  { num: "03", name: "Seamless onboarding", desc: "Our Adelaide team sets up monitoring, secures your devices and gets your team up to speed — with zero downtime." },
  { num: "04", name: "Proactive ongoing support", desc: "Your systems are proactively monitored, and help from a local engineer is a phone call away whenever you need it." },
];

const pillVariants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

const Home = () => {
  const [posts, setPosts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  useEffect(() => {
    axios.get(`${API}/blog`).then((res) => setPosts(res.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <>
      <SEO title="UMI IT Services | Adelaide Managed IT Support for SMEs" description="Fast, plain-English IT support, cybersecurity and cloud services for Adelaide small businesses. No lock-in contracts. Book a free consultation." />

      {/* Kinetic hero */}
      <section ref={heroRef} className="relative overflow-hidden bg-[#0B2545] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(55% 45% at 72% 28%, rgba(23,184,166,0.16), transparent 70%)" }} />
        <div className="pointer-events-none absolute inset-0 hero-grain opacity-[0.05]" />
        <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]"
            >
              Adelaide's local IT partner
            </motion.p>
            <h1 data-testid="hero-title" className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight leading-[1.05] text-white">
              <KineticLine delay={0.1}>IT support that</KineticLine>
              <KineticLine delay={0.24}>answers when</KineticLine>
              <KineticLine delay={0.38} className="text-[#17B8A6]">you call.</KineticLine>
            </h1>
            <motion.p
              data-testid="hero-subtitle"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
              className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl"
            >
              Proactive IT support, cybersecurity and cloud services for Adelaide small businesses — explained in plain English, with no lock-in contracts and no hidden fees.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.68, ease: EASE }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <MotionLink to="/contact" data-testid="hero-cta-consultation"
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                className="inline-flex items-center justify-center gap-2 bg-[#17B8A6] hover:bg-[#0D9488] text-white font-semibold px-7 py-3.5 rounded-lg transition-colors duration-200">
                Book a Free Consultation <ArrowRight size={18} aria-hidden="true" />
              </MotionLink>
              <MotionLink to="/services" data-testid="hero-cta-services"
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                className="inline-flex items-center justify-center gap-2 border-2 border-white/25 text-white hover:bg-white/10 font-semibold px-7 py-3.5 rounded-lg transition-colors duration-200">
                Explore Services
              </MotionLink>
            </motion.div>
            <motion.ul
              initial="hidden" animate="show"
              variants={{ show: { transition: { staggerChildren: 0.1, delayChildren: 0.85 } } }}
              className="mt-9 flex flex-wrap gap-2.5"
            >
              {["Fast, local response", "100% Adelaide-based", "No lock-in contracts"].map((pill) => (
                <motion.li key={pill} variants={pillVariants}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-slate-200">
                  <Check size={13} className="text-[#17B8A6]" aria-hidden="true" /> {pill}
                </motion.li>
              ))}
            </motion.ul>
          </div>

          <motion.div style={{ y: imgY }} className="relative">
            <div className="absolute -inset-0 translate-x-4 translate-y-4 rounded-2xl border-2 border-[#17B8A6]/40" aria-hidden="true" />
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.45, ease: EASE }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=75&w=1200"
                alt="Adelaide IT support team reviewing a small business technology plan together"
                className="rounded-2xl shadow-2xl w-full object-cover max-h-[70vh] lg:max-h-[520px]"
                loading="eager"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0B2545]/70 via-transparent to-[#0B2545]/10" aria-hidden="true" />
            </motion.div>
            <motion.div
              style={{ y: cardY }}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
              className="absolute -bottom-6 -left-4 sm:-left-6 flex items-center gap-3 rounded-xl bg-white shadow-xl px-5 py-4"
            >
              <Clock size={22} className="text-[#17B8A6]" aria-hidden="true" />
              <div>
                <p className="text-sm font-bold text-[#0B2545]">Fast local response</p>
                <p className="text-xs text-slate-500">Real engineers, right here in SA</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Editorial marquee */}
      <Marquee items={marqueeItems} />

      {/* Trust bar */}
      <section className="bg-white py-10 border-b border-slate-100" aria-label="Why businesses choose UMI">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Built for Adelaide businesses</p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {trustItems.map((item) => (
              <li key={item} className="flex items-center justify-center gap-2 text-sm font-medium text-slate-700 text-center">
                <ShieldCheck size={16} className="shrink-0 text-[#17B8A6]" aria-hidden="true" /> {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24 bg-[#F8FAFC]">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">What we do</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B2545] max-w-2xl">Four ways we keep your business running</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {services.map((s, i) => {
              const Icon = ICONS[s.slug];
              const testIds = { "managed-it-support": "services-card-managed-it", "cloud-cybersecurity": "services-card-cybersecurity", "network-infrastructure": "services-card-infrastructure", "custom-software": "services-card-custom-software" };
              return (
                <Reveal key={s.slug} delay={i * 0.08}>
                  <Link to={`/services/${s.slug}`} data-testid={testIds[s.slug]}
                    className="group block h-full rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 hover:border-[#17B8A6] hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
                    <div className="w-12 h-12 rounded-xl bg-[#0B2545] flex items-center justify-center">
                      <Icon size={22} className="text-[#17B8A6]" aria-hidden="true" />
                    </div>
                    <h3 className="mt-5 text-lg sm:text-xl font-semibold text-[#0B2545]">{s.title}</h3>
                    <p className="mt-2 text-base text-slate-600 leading-relaxed">{s.subtitle}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0D9488]">
                      Learn more <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why UMI — numbered manifesto chapters */}
      <section data-testid="why-umi-section" className="py-16 md:py-24 bg-white">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">The UMI manifesto</p>
              <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B2545]">Why Adelaide SMEs choose UMI</h2>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                We started UMI because Adelaide SMEs deserved better: IT support that speaks plainly, shows up fast, and never locks you in. That's the whole business model.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative mt-8">
                <div className="absolute -inset-0 translate-x-3 translate-y-3 rounded-2xl border-2 border-[#17B8A6]/40" aria-hidden="true" />
                <img
                  src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                  alt="UMI engineers collaborating with a small business client in an Adelaide office"
                  className="relative rounded-2xl shadow-lg w-full object-cover max-h-[70vh] lg:max-h-[300px]"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
          <ol className="lg:col-span-8">
            {whyPoints.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <li className="grid grid-cols-[auto_1fr] gap-5 sm:gap-8 border-t border-slate-200 py-8 last:border-b">
                  <span
                    className="font-heading text-5xl md:text-6xl font-extrabold leading-none select-none"
                    style={{ WebkitTextStroke: "1.5px #17B8A6", color: "transparent" }}
                    aria-hidden="true"
                  >
                    0{i + 1}
                  </span>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-[#0B2545]">{p.title}</h3>
                    <p className="mt-2 text-base text-slate-600 leading-relaxed">{p.desc}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-[#F1F5F9]">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">How it works</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B2545]">Simple 4-step onboarding</h2>
          </Reveal>
          <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {steps.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.08} className="h-full">
                <li data-testid={`how-it-works-step-${i + 1}`} className="relative h-full rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                  <span className="font-heading text-4xl font-extrabold text-[#17B8A6]/25">{step.num}</span>
                  <h3 className="mt-3 text-lg font-semibold text-[#0B2545]">{step.name}</h3>
                  <p className="mt-2 text-base text-slate-600 leading-relaxed">{step.desc}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Blog teaser */}
      {posts.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <Reveal>
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">From the blog</p>
                  <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B2545]">Plain-English IT guides for SMEs</h2>
                </div>
                <Link to="/blog" data-testid="blog-teaser-view-all" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0D9488] hover:text-[#17B8A6]">
                  View all articles <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {posts.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.08} className="h-full">
                  <Link to={`/blog/${p.slug}`} data-testid={i === 0 ? "blog-teaser-card" : `blog-teaser-card-${i + 1}`}
                    className="group block h-full rounded-2xl border border-slate-200 p-6 hover:border-[#17B8A6] hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
                    {p.image && (
                      <img src={p.image} alt={p.title} loading="lazy" className="-mx-6 -mt-6 mb-4 w-[calc(100%+3rem)] max-w-none h-36 object-cover rounded-t-2xl" />
                    )}
                    <p className="text-xs font-bold uppercase tracking-wider text-[#0D9488]">{p.category}</p>
                    <h3 className="mt-3 text-lg font-semibold text-[#0B2545] leading-snug">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                      <FileText size={13} aria-hidden="true" /> {p.reading_time} min read
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Regulated industries — separate practice, distinct tone */}
      <section className="relative overflow-hidden bg-[#07182E] py-16 md:py-24">
        <div className="pointer-events-none absolute inset-0 hero-grain opacity-[0.05]" />
        <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">For regulated industries</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-3xl">Compliance-grade technology, without big-consulting overhead</h2>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-3xl">
              Separate from our small-business IT services, UMI runs two dedicated practices for regulated organisations.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <Reveal delay={0.08} className="h-full">
              <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8">
                <h3 className="text-lg sm:text-xl font-semibold text-white">AUSTRAC Tranche 2 entities</h3>
                <p className="mt-2 text-base text-slate-300 leading-relaxed">AML/CTF compliance systems, privacy &amp; data governance, and complaints-handling support for newly regulated businesses — real estate, legal, conveyancing, accounting and AFSL/ACL holders.</p>
              </div>
            </Reveal>
            <Reveal delay={0.16} className="h-full">
              <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8">
                <h3 className="text-lg sm:text-xl font-semibold text-white">APRA-regulated institutions</h3>
                <p className="mt-2 text-base text-slate-300 leading-relaxed">Data-sovereign AI infrastructure and regulatory technology for mutual banks, credit unions and smaller non-bank lenders.</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <MotionLink to="/regulated-industries" data-testid="home-regulated-cta"
              whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
              className="mt-8 inline-flex items-center gap-2 border-2 border-white/25 text-white hover:bg-white/10 font-semibold px-7 py-3.5 rounded-lg transition-colors duration-200">
              Explore Regulated-Industry Services <ArrowRight size={18} aria-hidden="true" />
            </MotionLink>
          </Reveal>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative overflow-hidden bg-[#0B2545] py-16 md:py-24">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(50% 60% at 50% 100%, rgba(23,184,166,0.14), transparent 70%)" }} />
        <div className="pointer-events-none absolute inset-0 hero-grain opacity-[0.05]" />
        <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Ready for IT that just works?</h2>
            <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">Book a free 30-minute consultation. We'll review your setup, answer your questions in plain English, and give you a clear picture of your options.</p>
          </Reveal>
          <Reveal delay={0.12}>
            <div data-testid="home-cta-form" className="mt-10 max-w-2xl mx-auto rounded-2xl bg-white p-6 sm:p-8 text-left shadow-2xl">
              <ContactForm formLocation="home" compact />
            </div>
            <p className="mt-5 text-xs text-slate-400 flex items-center justify-center gap-1.5">
              <MapPin size={13} aria-hidden="true" /> Serving Adelaide &amp; greater metro South Australia
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Home;
